import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  LuUtensils,
  LuMusic,
  LuClock,
  LuPhone,
  LuChevronLeft,LuChevronRight,
} from "react-icons/lu";
import { FaChevronLeft, FaChevronRight, FaPlay, FaTimes } from "react-icons/fa";
import { LiaMapMarkerAltSolid } from "react-icons/lia";
import { useLanguage } from "../context/LanguageContext";


const Home = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(0);

  const heroImages = [
    "/images/DSC08495.webp",
    "/images/DSC08521.webp",
    "/images/DSC08535.webp",
    "/images/DSC08541.webp",
    "/images/DSC08635.webp",
  ];

  const features = [
    {
      icon: <LuUtensils className="text-5xl text-[#72201e]" />,
      titleKey: "home.moroccanCuisine",
      descriptionKey: "home.moroccanCuisineDesc",
    },
    {
      icon: <LuMusic className="text-5xl text-[#72201e]" />,
      titleKey: "home.showAmbiance",
      descriptionKey: "home.showAmbianceDesc",
    },
  ];

  // Auto-slide every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroImages.length) % heroImages.length
    );
  };

  return (
    <div className="home w-screen">
      {/* Hero Section with Carousel */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden w-full">
        {/* Carousel Images */}
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 overflow-hidden ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <div
              className="absolute inset-0 w-full h-full bg-cover bg-center"
              style={{
                backgroundImage: `url(${image})`,
                animation:
                  index === currentSlide
                    ? "zoomIn 10s ease-out forwards"
                    : "none",
              }}
            ></div>
            {/* Overlay */}
            <div className="absolute inset-0 w-full h-full bg-black bg-opacity-50"></div>
          </div>
        ))}

        {/* Content */}
        <div className="container mx-auto px-4 text-center text-white relative z-10">
          <div data-aos="fade-up">
            <h1 className="text-5xl md:text-7xl font-elegant font-bold mb-6">
              JANA MARRAKECH
            </h1>
            <p className="text-2xl md:text-3xl font-semibold mb-4 tracking-widest">
              {t('home.subtitle')}
            </p>
            <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto leading-relaxed uppercase">
              {t('home.heroDescription')}
            </p>
            <p className="text-lg md:text-xl mb-2 uppercase">{t('home.showAmbiance')}</p>
            <p className="text-lg md:text-xl mb-8 uppercase">{t('home.danseusesGnawa')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/reservation" className="btn-primary">
                {t('home.bookTable')}
              </Link>
              <Link to="/menu" className="btn-secondary">
                {t('home.exploreMenu')}
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 text-white p-3 md:p-4 rounded-full transition-all duration-300 z-20 backdrop-blur-sm"
          aria-label="Previous slide"
        >
          <LuChevronLeft className="text-xl md:text-2xl" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 text-white p-3 md:p-4 rounded-full transition-all duration-300 z-20 backdrop-blur-sm"
          aria-label="Next slide"
        >
          <LuChevronRight className="text-xl md:text-2xl" />
        </button>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-20 bg-marrakech-creamss bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-aos="fade-right" className="relative group cursor-pointer" onClick={() => setIsVideoOpen(true)}>
              <img
                src="/images/DSC08503.webp"
                alt="Jana Marrakech Interior"
                className="rounded-lg shadow-2xl w-full group-hover:brightness-75 transition-all duration-300"
              />
              {/* Play Button Overlay - Center */}
              <button onClick={() => {setIsVideoOpen(true);}} className="group absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24  rounded-full flex items-center justify-center  transition-all duration-300 hover:scale-110">
                <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping"></div>
                <div className="absolute inset-2 rounded-full border-2 border-white/50 animate-pulse"></div>
                <FaPlay className="w-8 h-8 text-white ml-1 group-hover:scale-110 transition-transform duration-300" />
              </button>
              {/* <button onClick={() => setIsVideoOpen(true)} className="group absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-[#9c904c] hover:bg-[#8a7f42] rounded-full flex items-center justify-center shadow-xl transition-all duration-300 hover:scale-110">
                <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping"></div>
                <div className="absolute inset-2 rounded-full border-2 border-white/50 animate-pulse"></div>
                <FaPlay className="w-8 h-8 text-white ml-1 group-hover:scale-110 transition-transform duration-300" />
              </button> */}

              {/* <button onClick={() => setIsVideoOpen(true)} className="group absolute -bottom-6 -right-6 w-24 h-24 bg-[#9c904c] hover:bg-[#8a7f42] rounded-full flex items-center justify-center shadow-xl transition-all duration-300 transform hover:scale-110">
                <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping"></div>
                <div className="absolute inset-2 rounded-full border-2 border-white/50 animate-pulse"></div>
                <FaPlay className="w-8 h-8 text-white ml-1 group-hover:scale-110 transition-transform duration-300" />
              </button> */}
            </div>
            <div data-aos="fade-left">
              <h2 className="text-4xl md:text-5xl font-elegant font-bold text-marrakech-dark mb-6">
                {t('home.welcome')}
              </h2>
              <p className="text-lg text-marrakech-clays text-black mb-6 leading-relaxed">
                {t('home.welcomeDesc1')}
              </p>
              <p className="text-lg text-marrakech-clays text-black mb-6 leading-relaxed">
                {t('home.welcomeDesc2')}
              </p>
              <Link to="/about" className="btn-primary inline-block">
                {t('home.learnMore')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-elegant font-bold text-marrakech-dark mb-4">
              {t('home.janaExperience')}
            </h2>
            <p className="text-lg text-marrakech-clay max-w-2xl mx-auto">
              {t('home.janaExperienceDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="bg-marrakech-cream p-8 rounded-lg text-center card-hover"
              >
                <div className="flex justify-center mb-6">{feature.icon}</div>
                <h3 className="text-2xl font-bold text-marrakech-dark mb-4">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-marrakech-clay">{t(feature.descriptionKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20 bg-marrakech-cream">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-elegant font-bold text-marrakech-dark mb-4">
              {t('home.galleryPhotos')}
            </h2>
            <p className="text-lg text-marrakech-clay">
              {t('home.galleryPhotosDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              "/images/DSC01909.webp",
              "/images/image-0000007.webp",
              "/images/DSC01930.webp",
              "/images/image-0000043.webp",
              "/images/DSC08361.webp",
              "/images/DSC08376.webp",
              "/images/DSC08495.webp",
              "/images/DSC08616.webp",
            ].map((image, index) => (
              <div
                key={index}
                data-aos="zoom-in"
                data-aos-delay={index * 50}
                className="image-zoom rounded-lg overflow-hidden shadow-lg"
              >
                <img
                  src={image}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-64 object-cover"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-12" data-aos="fade-up">
            <Link to="/gallery" className="btn-primary">
              {t('home.viewMorePhotos')}
            </Link>
          </div>
        </div>
      </section>

      {/* Hours & Location */}
      <section
        className="py-20 parallax overlay"
        style={{
          backgroundImage: "url(/images/DSC08705.webp)",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 text-white text-center">
            <div data-aos="fade-up">
              <LuClock className="text-4xl md:text-5xl text-[#eee1ca] mx-auto mb-4" />
              <h3 className="text-xl md:text-2xl font-bold mb-4">{t('home.hours')}</h3>
              <p className="text-base md:text-lg mb-2">{t('home.hoursOpen')}</p>
              <p className="text-base md:text-lg">{t('home.hoursWeekend')}</p>
            </div>
            <div data-aos="fade-up" data-aos-delay="100">
              <LiaMapMarkerAltSolid className="text-4xl md:text-5xl text-[#eee1ca] mx-auto mb-4" />
              <h3 className="text-xl md:text-2xl font-bold mb-4">{t('home.address')}</h3>
              <p className="text-base md:text-lg px-4 whitespace-pre-line">
                {t('home.addressText')}
              </p>
            </div>
            <div
              data-aos="fade-up"
              data-aos-delay="200"
              className="sm:col-span-2 lg:col-span-1"
            >
              <LuPhone className="text-4xl md:text-5xl text-[#eee1ca] mx-auto mb-4" />
              <h3 className="text-xl md:text-2xl font-bold mb-4">
                {t('home.reservation')}
              </h3>
              <a
                href="tel:+212644005467"
                className="text-base md:text-lg hover:text-marrakech-gold transition block mb-2"
              >
                +2126 44 00 54 67
              </a>
              <a
                href="mailto:reservation@janamarrakech.com"
                className="text-sm md:text-lg hover:text-marrakech-gold transition break-all px-4"
              >
                reservation@janamarrakech.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white text-black">
        <div className="container mx-auto px-4 text-center" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-elegant font-bold mb-6">
            {t('home.reserveTable')}
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            {t('home.reserveTableDesc')}
          </p>
          <a
            href="/reservation"
            className="btn-primary bg-white text-marrakech-terracotta  border-white"
          >
            {t('home.reserveNow')}
          </a>
        </div>
      </section>

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" onClick={() => {setIsVideoOpen(false);setCurrentVideo(0)}}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>

            {/* Close Button */}
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-marrakech-gold transition-colors duration-300 z-60"
              aria-label="Close video"
            >
              <FaTimes className="text-4xl" />
            </button>

            {/* Video Player */}
            {
              currentVideo === 0 ? (
                <video
                  key="video-0"
                  controls
                  autoPlay
                  className="w-full rounded-lg shadow-2xl"
                  style={{ maxHeight: '80vh' }}
                >
                  <source src="/files/jana-marrakech-video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <video
                  key="video-1"
                  controls
                  autoPlay
                  className="w-full rounded-lg shadow-2xl"
                  style={{ maxHeight: '80vh' }}
                >
                  <source src="/files/jana-marrakech-video-second.mov" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )
            }
            {/* <video
              controls
              autoPlay
              className="w-full rounded-lg shadow-2xl"
              style={{ maxHeight: '90vh' }}
            >
              <source src={currentVideo === 1 ? "/files/jana-marrakech-video.mp4" : "/files/jana-marrakech-video-second.mov"} type="video/mp4" />
              Your browser does not support the video tag.
            </video> */}

            {/* Left Navigation Button */}
            <button
              onClick={() => {setCurrentVideo(currentVideo === 0 ? 1 : 0);console.log(currentVideo);
              }}
              className="absolute left-0 top-1/2  text-white hover:text-marrakech-gold transition-colors duration-300 z-[999] hover:scale-125"
              aria-label="First video"
            >
              <FaChevronLeft className="text-5xl" />
            </button>

            {/* Right Navigation Button */}
            <button
              onClick={() => {setCurrentVideo(currentVideo === 0 ? 1 : 0);console.log(currentVideo);
              }}
              className="absolute right-0 top-1/2  text-white hover:text-marrakech-gold transition-colors duration-300 z-[999] hover:scale-125"
              aria-label="Second video"
            >
              <FaChevronRight className="text-5xl" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
